import {PullRequestStateIcons} from '@github-ui/issue-pr-state/Icons'
import {issueLinkedPullRequestHovercardPath} from '@github-ui/paths'
import type {IconProps} from '@primer/octicons-react'
import {ActionList, Link} from '@primer/react'
import {Tooltip} from '@primer/react/next'
import {clsx} from 'clsx'
import type {FC} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {LinkedPullRequest$data, LinkedPullRequest$key} from './__generated__/LinkedPullRequest.graphql'
import styles from './LinkedPullRequest.module.css'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const LinkedPullRequestPull = graphql`
  fragment LinkedPullRequest on PullRequest {
    url
    number
    state
    isDraft
    repository {
      name
      nameWithOwner
      owner {
        login
      }
    }
  }
`

type LinkedPullRequestType = {
  pullKey: LinkedPullRequest$key
  issueRepositoryName: string
  itemKey?: string
  omitIcon?: boolean
  omitHover?: boolean
}

export const LinkedPullRequestLinkItem: FC<LinkedPullRequestType> = props => {
  const {pullKey, issueRepositoryName, omitIcon, omitHover} = props
  const pull = useFragment(LinkedPullRequestPull, pullKey)
  const owner = pull.repository?.owner?.login
  const repoName = pull.repository?.name

  const isExternal = pull.repository.nameWithOwner.toLocaleLowerCase() !== issueRepositoryName.toLocaleLowerCase()

  const tooltipTitle = `${owner}/${repoName}#${pull.number}`
  const hoverCard = issueLinkedPullRequestHovercardPath({
    owner: pull.repository.owner.login,
    repo: pull.repository.name,
    pullRequestNumber: pull.number,
  })
  const linkElement = (
    <Link
      href={pull.url}
      data-hovercard-url={!omitHover ? hoverCard : undefined}
      target="_blank"
      role="link"
      className={clsx(
        styles.pullRequestLink,
        isExternal ? styles.pullRequestLinkExternal : styles.pullRequestLinkInternal,
      )}
    >
      {!omitIcon && <LinkedPRIcon pullKey={props.pullKey} />}
      <LinkedPRName {...props} />
    </Link>
  )

  // Not showing a tooltip for PRs in the same repo, as they can't be truncated so we know all of the information is already visible
  return !omitHover && isExternal ? <Tooltip text={tooltipTitle}>{linkElement}</Tooltip> : linkElement
}

export const LinkedPullRequestListItem: FC<LinkedPullRequestType> = props => {
  const {pullKey, itemKey, issueRepositoryName} = props
  const pull = useFragment(LinkedPullRequestPull, pullKey)

  // Always print full length PR name in ActionList
  const owner = pull.repository?.owner?.login
  const repoName = pull.repository?.name
  const isExternal = pull.repository.nameWithOwner.toLocaleLowerCase() !== issueRepositoryName.toLocaleLowerCase()
  const number = pull.number

  return (
    <ActionList.LinkItem href={pull.url} key={itemKey} target="_blank">
      <ActionList.LeadingVisual>
        <LinkedPRIcon pullKey={props.pullKey} />
      </ActionList.LeadingVisual>
      <div className={styles.pullRequestTitle}>{isExternal ? `${owner}/${repoName} #${number}` : `#${number}`}</div>
    </ActionList.LinkItem>
  )
}

export const LinkedPRIcon: FC<Pick<LinkedPullRequestType, 'pullKey'> & IconProps> = ({pullKey, ...props}) => {
  const pull = useFragment(LinkedPullRequestPull, pullKey)
  const PullStateIcon = getPrIcon(pull)

  // eslint-disable-next-line react-hooks/static-components
  return <PullStateIcon {...props} />
}

export const LinkedPRName: FC<LinkedPullRequestType> = ({pullKey, issueRepositoryName}) => {
  const pull = useFragment(LinkedPullRequestPull, pullKey)
  const owner = pull.repository?.owner?.login
  const repoName = pull.repository?.name

  const isExternal = pull.repository.nameWithOwner.toLocaleLowerCase() !== issueRepositoryName.toLocaleLowerCase()

  // This not straight-forward logic is to make sure that we are always showing the pull number and only truncating
  // the org/repo prefix
  const title = isExternal ? (
    <div className={styles.externalRepoContainer}>
      <div className={styles.repositoryName}>{`${owner}/${repoName}`}</div>
      <span>#{pull.number}</span>
    </div>
  ) : (
    <span>{`#${pull.number}`}</span>
  )

  return <>{title}</>
}

const getPrIcon = (pullRequest: LinkedPullRequest$data) => {
  // 'CLOSED' is filtered out of Linked PRs pill
  const state = pullRequest.state === 'MERGED' ? 'MERGED' : 'OPEN'
  const stateIcon = state === 'OPEN' && pullRequest.isDraft ? PullRequestStateIcons.DRAFT : PullRequestStateIcons[state]
  return stateIcon
}

try{ LinkedPRIcon.displayName ||= 'LinkedPRIcon' } catch {}
try{ PullStateIcon.displayName ||= 'PullStateIcon' } catch {}
try{ LinkedPRName.displayName ||= 'LinkedPRName' } catch {}